/* eslint-disable @next/next/no-img-element */
import { unit, C, Canvas, Abs, Avatar, BLogo, Icon, Sparkle } from './kit';

// Vector rebuild of /img/redesign/automate-visual-v2.png (1463×1075 canvas).
const W = 1463;
const H = 1075;
const u = unit(W);

const INK = '#16141F';
const GRAY = '#686A7C';
const ORANGE = '#F5650A';
const CARD_BG = '#FAF6F4';
const SHADOW = `0 ${u(14)} ${u(34)} rgba(60, 35, 20, 0.16), 0 ${u(2)} ${u(6)} rgba(60, 35, 20, 0.06)`;

// Text positioned by its left edge and visual centre line.
const T = ({ x, y, size, weight = 500, color = INK, style, children }) => (
  <Abs u={u} x={x} y={y - size * 0.56} style={{ fontSize: u(size), fontWeight: weight, color, lineHeight: 1, ...style }}>
    {children}
  </Abs>
);

// Floating card rotated about its centre; children use the card's own frame.
const Float = ({ x, y, w, h, rot, r = 22, bg = CARD_BG, children }) => (
  <Abs u={u} x={x} y={y} w={w} h={h} style={{
    background: bg, borderRadius: u(r), border: `${u(1.5)} solid #EFE8E5`, boxSizing: 'border-box',
    boxShadow: SHADOW, transform: `rotate(${rot}deg)`,
  }}>
    {/* Children are laid out from the outer edge, not the border box. */}
    <Abs u={u} x={-1.5} y={-1.5} w={w} h={h}>{children}</Abs>
  </Abs>
);

const Svg = ({ size, children, style }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
    style={{ width: u(size), height: u(size), display: 'block', flexShrink: 0, ...style }}>
    {children}
  </svg>
);

const UsersOutline = ({ size, color }) => (
  <Svg size={size} style={{ color }}>
    <g strokeWidth="2">
      <circle cx="9" cy="8" r="3.6" />
      <path d="M2.8 20v-.6c0-3.3 2.8-5.6 6.2-5.6s6.2 2.3 6.2 5.6v.6z" />
      <path d="M15.2 4.6a3.6 3.6 0 0 1 0 6.9M18.4 14.2c1.8.7 2.9 2.4 2.9 4.6v1.2" />
    </g>
  </Svg>
);

const AtSign = ({ size, color }) => (
  <Svg size={size} style={{ color }}>
    <g strokeWidth="2">
      <circle cx="12" cy="12" r="3.8" />
      <path d="M15.8 8.4v5.1a2.6 2.6 0 0 0 5.2 0V12a9 9 0 1 0-3.6 7.2" />
    </g>
  </Svg>
);

const OrgIcon = ({ size, color }) => (
  <Svg size={size} style={{ color }}>
    <g strokeWidth="1.9">
      <rect x="9" y="2.5" width="6" height="5" rx="0.8" />
      <rect x="2.5" y="16.5" width="6" height="5" rx="0.8" />
      <rect x="15.5" y="16.5" width="6" height="5" rx="0.8" />
      <path d="M12 7.5v4.5M5.5 16.5V12h13v4.5" />
    </g>
  </Svg>
);

const GCal = ({ size }) => (
  <svg viewBox="0 0 32 32" aria-hidden="true" style={{ width: u(size), height: u(size), display: 'block' }}>
    <rect x="1" y="1" width="30" height="30" rx="4" fill="#3E7CF0" />
    <path d="M1 24h24v7H5a4 4 0 0 1-4-4z" fill="#22A045" />
    <path d="M24 1h3a4 4 0 0 1 4 4v19h-7z" fill="#F9BC15" />
    <path d="M24 24h7l-7 7z" fill="#E8453C" />
    <rect x="7" y="7" width="17" height="17" fill="#fff" />
    <text x="15.5" y="20.6" textAnchor="middle" fontSize="11.5" fontWeight="700" fill="#3E7CF0" fontFamily="Arial, sans-serif">31</text>
  </svg>
);

// Dotted orange connectors (short rounded dashes, no heads).
const Dots = ({ paths }) => (
  <svg viewBox={`0 0 ${W} ${H}`} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible' }} aria-hidden="true">
    {paths.map(d => (
      <g key={d} fill="none" strokeLinecap="round">
        <path d={d} stroke="#FFE7CC" strokeWidth="7.5" strokeDasharray="7 11" />
        <path d={d} stroke="#F6A044" strokeWidth="4.5" strokeDasharray="7 11" />
      </g>
    ))}
  </svg>
);

// ---- Main #hr-requests card ---------------------------------------------

const AppBadge = ({ x, y }) => (
  <Abs u={u} x={x} y={y - 11.5} h={23} style={{
    display: 'flex', alignItems: 'center', padding: `0 ${u(6)}`, borderRadius: u(5),
    background: '#EFEAE8', fontSize: u(13.5), fontWeight: 700, color: INK, lineHeight: 1,
  }}>APP</Abs>
);

const Btn = ({ x, y, w, h = 38, color, border, bg = 'transparent', size = 18, children }) => (
  <Abs u={u} x={x} y={y - h / 2} w={w} h={h} style={{
    display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box',
    border: `${u(1.5)} solid ${border}`, borderRadius: u(7), background: bg,
    fontSize: u(size), fontWeight: 600, color, lineHeight: 1,
  }}>{children}</Abs>
);

const MascotTile = ({ cx, cy, src, img, bg, glow }) => (
  <>
    <Abs u={u} x={cx - 29} y={cy - 29} w={58} h={58} style={{
      borderRadius: u(13), background: bg, boxShadow: glow ? `0 0 ${u(12)} ${glow}` : undefined,
    }} />
    <Abs u={u} x={cx - img / 2} y={cy - img / 2} w={img} h={img}>
      <img src={`/img/redesign/mascots/${src}.webp`} alt="" draggable="false" style={{ width: '100%', height: '100%', display: 'block' }} />
    </Abs>
  </>
);

const Message = ({ y, name, time, appX, timeX, line1, line2 }) => (
  <>
    <T x={246} y={y} size={20.5} weight={700}>{name}</T>
    <AppBadge x={appX} y={y} />
    <T x={timeX} y={y} size={15.5} color={GRAY}>{time}</T>
    <T x={246} y={y + 35} size={19.5} weight={600}>{line1}</T>
    <T x={246} y={y + 68} size={18.5}>{line2}</T>
  </>
);

const MainCard = () => (
  <Abs u={u} x={173} y={72} w={828} h={723} style={{
    background: CARD_BG, borderRadius: u(26), boxSizing: 'border-box',
    border: `${u(1.5)} solid #E6DEDA`,
    boxShadow: `inset 0 0 0 ${u(5)} #FDFBFA, inset 0 0 0 ${u(6.5)} #EEE7E3, ${SHADOW}`,
    transform: 'rotate(-0.6deg) skewY(-0.5deg)',
  }}>
    <Abs u={u} x={-1.5} y={-1.5} w={828} h={723}>
      {/* Sidebar */}
      <Abs u={u} x={10} y={10} w={88} h={626} style={{
        borderRadius: u(20), background: 'linear-gradient(180deg, #130D27 0%, #160F2C 55%, #221839 100%)',
      }} />
      <Abs u={u} x={56 - 29} y={62 - 29}><BLogo u={u} size={58} /></Abs>
      {[['home', 150], ['bot', 221], ['bell', 293]].map(([n, cy]) => (
        <Abs key={n} u={u} x={56 - 17} y={cy - 17}><Icon u={u} name={n} size={34} color="#fff" stroke={2.4} /></Abs>
      ))}
      <Abs u={u} x={56 - 29} y={369 - 29} w={58} h={58} style={{
        borderRadius: u(13), background: '#6A46FB', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
      }}>
        <Icon u={u} name="hash" size={32} stroke={2.8} />
      </Abs>
      <Abs u={u} x={56 - 16} y={441 - 16}><Icon u={u} name="plus" size={32} color="#fff" stroke={1.8} /></Abs>
      <Abs u={u} x={57 - 29} y={581 - 29}><Avatar u={u} name="me" size={58} ring={2} dot="#1E1636" /></Abs>

      {/* Channel header */}
      <T x={163} y={47} size={25} weight={700}># hr-requests</T>
      <Abs u={u} x={600} y={22} w={136} h={37} style={{
        borderRadius: u(19), border: `${u(1.5)} solid #EAE3E0`, boxSizing: 'border-box',
      }} />
      {['neha', 'priya', 'arjun'].map((a, i) => (
        <Abs key={a} u={u} x={603 + i * 27.5} y={24}>
          <Avatar u={u} name={a} size={33} ring={2} style={{ background: '#A9CBF0', borderRadius: '50%' }} />
        </Abs>
      ))}
      <T x={700} y={41} size={18} weight={600}>12</T>
      <Abs u={u} x={766 - 13} y={40 - 13}><UsersOutline size={26} color="#575A70" /></Abs>

      {/* Palmy */}
      <MascotTile cx={193} cy={141} src="palmy" img={50} bg="#F5EEEB" />
      <Message y={122} name="Palmy" appX={313} timeX={374} time="10:30 AM"
        line1="PTO request from Neha (May 22 – May 24)" line2="3 days • Personal" />
      <Btn x={245} y={235} w={104} h={40} color="#1E9A3A" border="#BFE2C8">Approve</Btn>
      <Btn x={363} y={235} w={92} h={39} color="#E52222" border="#F2C2C1">Decline</Btn>
      <Btn x={471} y={234} w={128} h={37} color={INK} border="#E3DCD9">View details</Btn>

      {/* Linky */}
      <MascotTile cx={193} cy={322} src="linky" img={60} bg="#DDF3F0" glow="rgba(140, 220, 210, 0.45)" />
      <Message y={305} name="Linky" appX={307} timeX={368} time="10:32 AM"
        line1="Updated org chart" line2="2 new hires added to Marketing" />
      <Btn x={244} y={413} w={150} color={ORANGE} border="#F8DCC6">View org chart</Btn>

      {/* Stany */}
      <MascotTile cx={192} cy={501} src="stany" img={56} bg="#F6EAF3" glow="rgba(230, 190, 235, 0.45)" />
      <Message y={482} name="Stany" appX={310} timeX={371} time="10:35 AM"
        line1="Standup summary is ready" line2="5 updates from #team-standup" />
      <Btn x={244} y={591} w={149} color={ORANGE} border="#F8DCC6">View summary</Btn>

      {/* Composer */}
      <Abs u={u} x={196} y={641} w={580} h={60} style={{
        borderRadius: u(12), border: `${u(1.5)} solid #E6E0DD`, boxSizing: 'border-box',
        boxShadow: `0 ${u(3)} ${u(8)} rgba(60, 35, 20, 0.05)`,
      }} />
      <Abs u={u} x={224 - 13} y={671 - 13}><Icon u={u} name="plus" size={26} color={GRAY} stroke={1.8} /></Abs>
      <Abs u={u} x={250} y={656} w={1.5} h={30} style={{ background: '#E6E0DD' }} />
      <T x={270} y={671} size={17.5} color={GRAY}>Message #hr-requests</T>
      <Abs u={u} x={703 - 15} y={673 - 15}><Icon u={u} name="smile" size={30} color={GRAY} stroke={1.9} /></Abs>
      <Abs u={u} x={745 - 15} y={673 - 15}><AtSign size={30} color={GRAY} /></Abs>
    </Abs>
  </Abs>
);

// ---- Floating cards ------------------------------------------------------

const CalendarCard = () => (
  <Float x={1061} y={105} w={366} h={248} rot={2.75}>
    <Abs u={u} x={26} y={29}><GCal size={32} /></Abs>
    <T x={77} y={44} size={18} weight={700}>Calendar Update</T>
    <Abs u={u} x={276} y={35} w={9} h={9} style={{ borderRadius: '50%', background: '#14A33D' }} />
    <T x={290} y={39.5} size={15} color="#14A33D">Synced</T>
    <T x={33} y={100} size={16} weight={700}>PTO approved: Neha</T>
    <T x={33} y={125} size={16} color="#4B4D60">May 22 – May 24</T>
    {[['THU', 82], ['FRI', 179], ['SAT', 278]].map(([d, cx]) => (
      <Abs key={d} u={u} x={cx - 25} y={164} w={50} style={{ textAlign: 'center', fontSize: u(11.5), color: GRAY, lineHeight: 1 }}>{d}</Abs>
    ))}
    {[[35, 95], [132, 94], [230, 96]].map(([x, w]) => (
      <Abs key={x} u={u} x={x} y={185} w={w} h={35} style={{ borderRadius: u(6), background: '#FAF0E9' }} />
    ))}
    {[[35, 95], [132, 94]].map(([x, w]) => (
      <Abs key={x} u={u} x={x} y={200} w={w} h={17} style={{ borderRadius: u(5), background: '#FC851A' }} />
    ))}
  </Float>
);

const OrgCard = () => (
  <Float x={1081.6} y={394.6} w={354} h={296} rot={2.98}>
    <Abs u={u} x={27} y={24}><OrgIcon size={38} color="#F7790F" /></Abs>
    <T x={83} y={44} size={18} weight={700}>Org Chart Updated</T>
    {[['rohan', 'Rohan Singh', 'Product Designer', 88], ['aisha', 'Aisha Khan', 'Marketing Manager', 171]].map(([a, n, role, y]) => (
      <Abs key={a} u={u} x={0} y={y} w={354} h={90}>
        <Abs u={u} x={25} y={0}><Avatar u={u} name={a} size={44} /></Abs>
        <T x={84} y={9} size={16} weight={700}>{n}</T>
        <T x={84} y={32} size={14.5} color={GRAY}>{role}</T>
        <T x={84} y={54} size={14.5} color={GRAY}>Start date: May 20</T>
      </Abs>
    ))}
    <T x={25} y={267} size={14.5} color={GRAY}>+2 more updates</T>
  </Float>
);

const StandupCard = () => (
  <Float x={1046.4} y={744.1} w={376} h={251} rot={3.3}>
    <Abs u={u} x={23} y={20} w={35} h={35} style={{ borderRadius: u(9), background: '#F5E9F6' }} />
    <Abs u={u} x={21} y={18} w={39} h={39}>
      <img src="/img/redesign/mascots/stany.webp" alt="" draggable="false" style={{ width: '100%', height: '100%', display: 'block' }} />
    </Abs>
    <T x={70} y={37} size={16} weight={700}>Standup Summary</T>
    <Abs u={u} x={192} y={35 - 14.5 * 0.56} w={160} style={{ textAlign: 'right', fontSize: u(14.5), color: GRAY, lineHeight: 1 }}>Today, 10:35 AM</Abs>
    <T x={29} y={85} size={14.5} weight={600}>5 team members shared updates</T>
    {[['#13B03E', '3 are on track', 114], ['#FD8F1A', '1 blocked', 142], ['#8250E6', '1 needs follow-up', 170]].map(([c, t, y]) => (
      <Abs key={t} u={u} x={0} y={0}>
        <Abs u={u} x={23.5} y={y - 3.5} w={7} h={7} style={{ borderRadius: '50%', background: c }} />
        <T x={37} y={y} size={14.5}>{t}</T>
      </Abs>
    ))}
    <Abs u={u} x={22} y={200} w={138} h={30} style={{
      borderRadius: u(7), background: '#FDEEE2', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: u(14.5), fontWeight: 600, color: ORANGE, lineHeight: 1,
    }}>View full summary</Abs>
  </Float>
);

const TimeSavedCard = () => (
  <Float x={475.3} y={832.6} w={370} h={198} rot={2.85} bg="#FCFAF9">
    <T x={29} y={37} size={17} weight={700}>HR Time Saved</T>
    <Abs u={u} x={164 - 9} y={37 - 9}><Icon u={u} name="info" size={18} color={GRAY} stroke={1.8} /></Abs>
    <Abs u={u} x={29} y={87 - 31 * 0.56} style={{ display: 'flex', alignItems: 'baseline', gap: u(6), lineHeight: 1, letterSpacing: '-0.01em' }}>
      <span style={{ fontSize: u(32), fontWeight: 700 }}>23.5</span>
      <span style={{ fontSize: u(23), fontWeight: 700 }}>hrs</span>
    </Abs>
    <T x={29} y={125} size={15}>Saved this week</T>
    <Abs u={u} x={29} y={155 - 14.5 * 0.56} style={{ display: 'flex', alignItems: 'center', gap: u(4), fontSize: u(14.5), lineHeight: 1, color: GRAY }}>
      <Icon u={u} name="arrowUp" size={14} color="#14963A" stroke={2.6} />
      <span style={{ color: '#14963A', fontWeight: 700 }}>68%</span> from last week
    </Abs>
    <svg viewBox="0 0 370 198" aria-hidden="true" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible' }}>
      <defs>
        <linearGradient id="automate-saved-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#FC8A26" stopOpacity="0.18" />
          <stop offset="1" stopColor="#FC8A26" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d="M192 133 C202 124 208 117 218 117 C228 117 230 123 240 117 C250 110 254 99 266 98 C278 97 280 104 290 98 C300 92 310 80 332 71 L332 160 L192 160 Z" fill="url(#automate-saved-fill)" />
      <path d="M192 133 C202 124 208 117 218 117 C228 117 230 123 240 117 C250 110 254 99 266 98 C278 97 280 104 290 98 C300 92 310 80 332 71" fill="none" stroke="#F9861C" strokeWidth="4" strokeLinecap="round" />
      <circle cx="332" cy="71" r="7.5" fill="#F9861C" />
    </svg>
  </Float>
);

const InboxRow = ({ y, icon, title, sub }) => (
  <>
    <Abs u={u} x={22} y={y - 14} w={38} h={38} style={{ borderRadius: u(9), background: '#EFEBEA' }} />
    {icon}
    <T x={74} y={y} size={14.5} weight={600}>{title}</T>
    <T x={74} y={y + 21} size={14} color={GRAY}>{sub}</T>
    <Abs u={u} x={253} y={y - 4} w={58} h={26} style={{
      borderRadius: u(6), background: '#FFEEDF', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: u(13), fontWeight: 600, color: '#F7701C', lineHeight: 1,
    }}>Pending</Abs>
  </>
);

const InboxCard = () => (
  <Float x={28.5} y={717.5} w={333} h={293} rot={-5.6} r={20}>
    <T x={24} y={35.5} size={16.5} weight={700}>Requests Inbox</T>
    <Abs u={u} x={150} y={26} w={22} h={19} style={{
      borderRadius: u(6), background: '#EFEBEA', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: u(12.5), fontWeight: 700, lineHeight: 1,
    }}>5</Abs>
    <InboxRow y={84} title="PTO Request – Neha" sub="May 22 – May 24" icon={(
      <Abs u={u} x={23} y={69} w={36} h={38}>
        <img src="/img/redesign/mascots/palmy.webp" alt="" draggable="false" style={{ width: '100%', height: '100%', display: 'block' }} />
      </Abs>
    )} />
    <InboxRow y={142} title="Work From Home – Arjun" sub="May 21" icon={(
      <Abs u={u} x={24} y={130}><Avatar u={u} name="arjun" size={34} /></Abs>
    )} />
    <InboxRow y={200} title="Leave Request – Priya" sub="May 30 – May 31" icon={(
      <Abs u={u} x={24} y={188}><Avatar u={u} name="priya" size={34} /></Abs>
    )} />
    <Abs u={u} x={1.5} y={243} w={330} h={1.5} style={{ background: '#F0EAE7' }} />
    <T x={23} y={265} size={15.5} weight={600} color={ORANGE}>View all requests</T>
  </Float>
);

export default function AutomateVisual({ className }) {
  return (
    <Canvas W={W} H={H} className={className} label="HR automation in Slack: Palmy, Linky and Stany handle PTO approvals, org-chart updates and async standup summaries, saving HR hours every week">
      <Dots paths={[
        'M1008 290 C1022 278 1024 258 1028 246 C1032 232 1042 225 1060 224',
        'M1002 433 C1030 432 1046 440 1056 455 C1066 472 1072 480 1088 481',
        'M1010 730 C1022 742 1024 760 1030 774 C1035 784 1042 786 1050 786',
        'M99 595 C80 612 68 638 70 662 C72 685 82 704 92 720',
        'M924 799 L932 821',
      ]} />

      <Sparkle u={u} x={91} y={157} size={23} color="#F5A443" />
      <Sparkle u={u} x={74} y={215} size={21} color="#8C6CF0" />
      <Sparkle u={u} x={1452} y={350} size={23} color="#F7A040" />

      <MainCard />
      <CalendarCard />
      <OrgCard />
      <StandupCard />
      <TimeSavedCard />
      <InboxCard />

      <Abs u={u} x={864} y={829} w={134} h={156}>
        <img src="/img/redesign/visual-parts/automate-robot.webp" alt="" draggable="false" style={{ width: '100%', height: '100%', display: 'block' }} />
      </Abs>
    </Canvas>
  );
}
